import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import countries from 'i18n-iso-countries';

import {
  INSTANCE_DFLT_LANGUAGE,
  INSTANCE_FIXED_LANGUAGE,
} from './utils/instanceConstant';

// Supported languages for internationalization
const SUPPORTED_LANGUAGES = [
  { locale: 'en', countryCode: 'en', name: 'English', flag: '🇬🇧', menu: true },
  {
    locale: 'nl',
    countryCode: 'nl',
    name: 'Nederlands',
    flag: '🇳🇱',
    menu: true,
  },
  { locale: 'vi', countryCode: 'vi', name: 'Vietnam', flag: '🇻🇳', menu: false },
];

const getCountryCodeFromLocale = (locale) => locale?.slice(0, 2) || '';

// find the entry in our SUPPORTED_LANGUAGES array that
// best matches the given locale (or use current i18 next language
// locale if nothing passed in)
const findCurrentSupportedLanguageEntry = (locale) => {
  const currLocale = locale || i18next.language;
  const currCountryCode = currLocale.slice(0, 2).toLowerCase();

  // could e.g. be en-GB or en that we're looking for
  // prioritise full locale first, then country code,
  // then failing that return the default locale
  return (
    SUPPORTED_LANGUAGES.find((lang) => lang.locale === currLocale) ||
    SUPPORTED_LANGUAGES.find((lang) => lang.countryCode === currCountryCode) ||
    defaultLngEntry
  );
};

// does the given locale match the given SUPPORTED_LANGUAGES entry?
const isLanguageEntry = (langItem, locale = null) => {
  locale = locale || i18next.language;
  if (!locale) {
    return false;
  }
  return langItem.locale === locale;
};

// the default language is the one used if the detected language isn't
// in our list of supported languages
// NB we assume that INSTANCE_DFLT_LANGUAGE or 'en' is always in
// our supported languages list!
const defaultLng = (INSTANCE_DFLT_LANGUAGE || 'en').toLowerCase();
const defaultLngEntry = findCurrentSupportedLanguageEntry(defaultLng);

// Import country translations
import enCountries from 'i18n-iso-countries/langs/en.json';
import nlCountries from 'i18n-iso-countries/langs/nl.json';
import viCountries from 'i18n-iso-countries/langs/vi.json';

// Register the languages with the countries library
countries.registerLocale(enCountries);
countries.registerLocale(nlCountries);
countries.registerLocale(viCountries);

import en from './locales/en/translation.json';
import nl from './locales/nl/translation.json';
import vi from './locales/vi/translation.json';

// nb: the following init() completes synchronously - this is important
// to note as any code following the initial import (in src/index.jsx)
// can reply on i18n being "ready to go"
// if in future initialisation becomes async (http translations etc)
// then using code has to be considered
i18next
  .use(initReactI18next) // connects with React
  .use(LanguageDetector)
  .init({
    resources: {
      en: { translation: en },
      nl: { translation: nl },
      vi: { translation: vi },
    },
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // React handles XSS
    },
  });

if (INSTANCE_FIXED_LANGUAGE) {
  // language is fixed so force current language to be the default one
  i18next.changeLanguage(defaultLng);
} else {
  // if we don't have an exact match on locale for i18next current language,
  // then switch languages to the best match (might be same country, might
  // be the default locale)
  const bestMatchForCurrentLocale = findCurrentSupportedLanguageEntry();
  if (bestMatchForCurrentLocale.locale !== i18next.language) {
    // NB this is async, and we can't await at top level of ESM, so possible
    // ramifications for code that uses t() prematurely - overall I think we're ok
    i18next.changeLanguage(bestMatchForCurrentLocale.locale);
  }
}

// split out "t" for export
const { t } = i18next;

////////////////////////////////////////////////////////////
//
// i18n-related helpers
//

const formatList = (items, lng = i18next.language) =>
  new Intl.ListFormat(lng, {
    type: 'conjunction',
  }).format(items);

export {
  countries,
  SUPPORTED_LANGUAGES,
  getCountryCodeFromLocale,
  findCurrentSupportedLanguageEntry,
  isLanguageEntry,
  formatList,
  t,
};

export default i18next;
